// Humane copy for the failure modes a softphone hits in the
// browser. Mic access is mandatory: a denied prompt or a
// busy device means the call cannot connect, and the user
// needs to be told what to do — not shown a DOMException
// name (CLAUDE.md: "No 'Error 422'").

// Maps a getUserMedia / device error to user-facing copy.
export function microphoneErrorMessage(error: unknown): string {
  const name = errorName(error)

  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return (
        "We need your microphone to place calls. Allow mic " +
        "access in your browser, then try again."
      )
    case "NotReadableError":
    case "AbortError":
      return (
        "Your microphone is being used by another app. Close " +
        "it and try again."
      )
    case "NotFoundError":
    case "OverconstrainedError":
      return (
        "We couldn't find a microphone. Plug one in or check " +
        "your audio settings, then try again."
      )
    default:
      return "We couldn't access your microphone. Please try again."
  }
}

// Maps a connection failure to user-facing copy.
export function connectionErrorMessage(error: unknown): string {
  const name = errorName(error)

  if (
    name.includes("Network") ||
    name === "ConnectionNetworkError" ||
    name === "LoginNetworkIssuesError"
  ) {
    return "Lost connection to the call service. Reconnecting…"
  }

  if (
    name.includes("Login") ||
    name === "AuthenticationError"
  ) {
    return (
      "We couldn't sign you in for calls. Reach out to your " +
      "admin if this keeps happening."
    )
  }

  return "Couldn't connect for calls. We'll keep trying."
}

// Autoplay copy — when the browser blocks programmatic
// audio playback until a user gesture. The remote-audio
// renderer surfaces this; the UI shows a tap-to-enable
// affordance.
export const AUTOPLAY_BLOCKED_MESSAGE =
  "Tap anywhere to turn on call audio."

function errorName(error: unknown): string {
  if (error instanceof Error) return error.name || error.message
  if (typeof error === "string") return error
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    typeof (error as { name: unknown }).name === "string"
  ) {
    return (error as { name: string }).name
  }
  return ""
}

export function isAutoplayError(error: unknown): boolean {
  return errorName(error) === "NotAllowedError"
}
